"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const PageContexts_1 = require("../Contexts/PageContexts");
const mapHelpers_1 = require("../utils/mapHelpers");
const lodash_1 = __importDefault(require("lodash"));
const pathHelpers_1 = require("../utils/pathHelpers");
/**
 *  This component will build the validation function used to validate a form control's value. This function is then passed on to be used by the final-form field for rendering.
 * ### Note ###
 * This component requires the evaluation of the form values whether or not the validation is specified or used, so it is highly recommended that the ValidationOptimizer be used
 * instead of this Component directly.
 * @param controlDefinition The definition of this control. This is where the definitions for the various validation maps can be found
 * @param render The render prop used to render the control. This component provides the render prop with the validation function if one is appropriate, or undefined if not
 */
const ValidationRender = (props) => {
    const { controlDefinition: { warningMap, errorMap, label }, render, displayMap } = props;
    const formDataMapping = (0, PageContexts_1.usePageDataMappingSources)(); // get any supplemental state to pass to data mapper
    const { functionDefinitions } = (0, PageContexts_1.useControlProvider)();
    const [prevErr, setPrevErr] = (0, react_1.useState)([]);
    const [prevWarn, setPrevWarn] = (0, react_1.useState)([]);
    const [prevDisp, setPrevDisp] = (0, react_1.useState)([]);
    const errorRefs = (0, react_1.useMemo)(() => {
        if (errorMap) {
            let result = {};
            lodash_1.default.each(errorMap, (map) => {
                const newRefs = (0, pathHelpers_1.getRefs)(map, ['FORM', 'FORMVALUE', 'STATE']);
                if (Object.keys(newRefs).length > 0) {
                    result = lodash_1.default.merge(result, newRefs);
                }
            });
            return result;
        }
        else {
            return {};
        }
    }, [errorMap]);
    const displayRefs = (0, react_1.useMemo)(() => {
        if (displayMap) {
            let result = {};
            const newRefs = (0, pathHelpers_1.getRefs)(displayMap, ['FORM', 'FORMVALUE', 'STATE']);
            if (Object.keys(newRefs).length > 0) {
                result = lodash_1.default.merge(result, newRefs);
            }
            return result;
        }
        else {
            return {};
        }
    }, [displayMap]);
    const [firstMapping, setFirstMapping] = (0, react_1.useState)(false);
    const warningRefs = (0, react_1.useMemo)(() => {
        if (warningMap) {
            let result = {};
            lodash_1.default.each(warningMap, (map) => {
                const newRefs = (0, pathHelpers_1.getRefs)(map, ['FORM', 'FORMVALUE', 'STATE']);
                if (Object.keys(newRefs).length > 0) {
                    result = lodash_1.default.merge(result, newRefs);
                }
            });
            return result;
        }
        else {
            return {};
        }
    }, [warningMap]);
    const [previousState, setPreviousState] = (0, react_1.useState)({});
    const [previousValue, setPreviousValue] = (0, react_1.useState)({});
    const [previousRecordState, setPreviousRecordState] = (0, react_1.useState)({});
    // we're going to create a validation function that returns errors and warnings
    const validationFn = (0, react_1.useCallback)(async (value, allValues, _ignoreTouched, path) => {
        var _a, _b, _c;
        // now we need to build a set of data sources for our datamap
        let errors = [];
        let warnings = [];
        let displayOptions = {};
        //only check if the field has been interacted with, plus allow override for modal validation
        const parts = (path || '').split('.');
        const t = parts.map((part, idx) => {
            let target = allValues;
            let name = '';
            for (let i = 0; i < idx; i++) {
                target = lodash_1.default.get(target, parts[i]);
                name += parts[i].replace(/\[\d+\]/, '') + '.';
            }
            target = target ? lodash_1.default.get(target, part) : undefined;
            name += part.replace(/\[\d+\]/, '');
            return { [name]: target };
        });
        let formValue = { value };
        for (let i = 0; i < t.length; i++) {
            formValue = Object.assign(Object.assign({}, formValue), t[i]);
        }
        const currentRecordState = (_c = (_b = (_a = formDataMapping.accessors).STATE) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : {};
        const recordStateSnapshot = currentRecordState ? { value: currentRecordState.value, context: currentRecordState.context } : {};
        // only
        if (errorMap || warningMap || displayMap) {
            const accessors = Object.assign(Object.assign({}, formDataMapping.accessors), { FORM: () => {
                    return allValues;
                }, FORMVALUE: () => formValue });
            if (errorMap &&
                (!firstMapping ||
                    (errorRefs &&
                        ((0, pathHelpers_1.anyValuesChanged)(errorRefs['FORMVALUE'], previousValue, formValue) ||
                            (0, pathHelpers_1.anyValuesChanged)(errorRefs['FORM'], previousState, allValues) ||
                            (0, pathHelpers_1.anyValuesChanged)(errorRefs['STATE'], previousRecordState, currentRecordState))))) {
                const foundErrors = await (0, mapHelpers_1.fieldErrorMapEvaluator)(accessors, functionDefinitions, errorMap);
                errors.push(...foundErrors);
                setPrevErr(errors);
            }
            else {
                errors = prevErr;
            }
            if (warningMap &&
                (!firstMapping ||
                    (warningRefs &&
                        ((0, pathHelpers_1.anyValuesChanged)(warningRefs['FORMVALUE'], previousValue, formValue) ||
                            (0, pathHelpers_1.anyValuesChanged)(warningRefs['FORM'], previousState, allValues) ||
                            (0, pathHelpers_1.anyValuesChanged)(warningRefs['STATE'], previousRecordState, currentRecordState))))) {
                const foundWarnings = await (0, mapHelpers_1.fieldWarningMapEvaluator)(accessors, functionDefinitions, warningMap);
                warnings.push(...foundWarnings);
                setPrevWarn(warnings);
            }
            else {
                warnings = prevWarn;
            }
            if (displayMap &&
                (!firstMapping ||
                    (displayRefs &&
                        ((0, pathHelpers_1.anyValuesChanged)(displayRefs['FORMVALUE'], previousValue, formValue) ||
                            (0, pathHelpers_1.anyValuesChanged)(displayRefs['FORM'], previousState, allValues) ||
                            (0, pathHelpers_1.anyValuesChanged)(displayRefs['STATE'], previousRecordState, currentRecordState))))) {
                displayOptions = (await (0, mapHelpers_1.fieldDisplayMapEvaluator)(accessors, functionDefinitions, displayMap))[0];
                setPrevDisp(lodash_1.default.cloneDeep(displayOptions));
            }
            else {
                displayOptions = prevDisp;
            }
            if (!lodash_1.default.isEqual(previousState, allValues)) {
                setPreviousState(lodash_1.default.cloneDeep(allValues));
            }
            if (!lodash_1.default.isEqual(previousValue, formValue)) {
                setPreviousValue(lodash_1.default.cloneDeep(formValue));
            }
            if (!lodash_1.default.isEqual(previousRecordState, recordStateSnapshot)) {
                setPreviousRecordState(recordStateSnapshot);
            }
            if (!firstMapping) {
                setFirstMapping(true);
            }
        }
        return { errors, warnings, displayOptions };
    }, [
        errorMap,
        warningMap,
        displayMap,
        formDataMapping.accessors,
        functionDefinitions,
        prevErr,
        prevWarn,
        prevDisp,
        firstMapping,
        previousState,
        previousValue,
        warningRefs,
        displayRefs,
        errorRefs,
        previousRecordState,
    ]);
    if (!warningMap && !errorMap && !displayMap) {
        return render(undefined); //<Component {...props} />;
    }
    else {
        return render(validationFn);
    }
};
exports.default = ValidationRender;
